import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  Tooltip,
  useColorModeValue,
  Divider,
  Card,
  CardBody,
  Icon,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiUser, FiMapPin, FiDollarSign, FiUserCheck } from 'react-icons/fi';
import { BookingWithCustomer, User } from '../../services/firestore';

// Cast icons as React.ElementType to fix TypeScript issues
const FiChevronLeftIcon = FiChevronLeft as React.ElementType;
const FiChevronRightIcon = FiChevronRight as React.ElementType;
const FiCalendarIcon = FiCalendar as React.ElementType;
const FiClockIcon = FiClock as React.ElementType;
const FiUserIcon = FiUser as React.ElementType;
const FiMapPinIcon = FiMapPin as React.ElementType;
const FiDollarSignIcon = FiDollarSign as React.ElementType;
const FiUserCheckIcon = FiUserCheck as React.ElementType;

interface BookingsCalendarProps {
  bookings: BookingWithCustomer[];
  technicians: User[];
  onBookingClick: (booking: BookingWithCustomer) => void;
}

type CalendarView = 'month' | 'day';

const BookingsCalendar: React.FC<BookingsCalendarProps> = ({
  bookings,
  technicians,
  onBookingClick,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const todayBg = useColorModeValue('blue.50', 'blue.900');
  const selectedBg = useColorModeValue('blue.100', 'blue.800');

  // Helper to format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get calendar days for the current month
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    const endDate = new Date(lastDay);
    
    // Adjust to start on Sunday
    startDate.setDate(startDate.getDate() - startDate.getDay());
    // Adjust to end on Saturday
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    
    const days: Date[] = [];
    const currentDay = new Date(startDate);
    
    while (currentDay <= endDate) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date: Date) => {
    return bookings.filter(booking => {
      const bookingDate = booking.scheduledTime || booking.scheduledDate;
      if (!bookingDate) return false;
      
      // Convert Firestore Timestamp to Date
      let bDate: Date;
      if (bookingDate && typeof bookingDate === 'object' && 'toDate' in bookingDate) {
        bDate = (bookingDate as any).toDate();
      } else {
        bDate = new Date(bookingDate as any);
      }
      
      return (
        bDate.getDate() === date.getDate() &&
        bDate.getMonth() === date.getMonth() &&
        bDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Check if date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Check if date is selected
  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'confirmed': return 'blue';
      case 'in_progress': return 'orange';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  // Get customer name
  const getCustomerName = (booking: BookingWithCustomer) => {
    if (booking.customer?.firstName && booking.customer?.lastName) {
      return `${booking.customer.firstName} ${booking.customer.lastName}`;
    } else if (booking.customer?.firstName) {
      return booking.customer.firstName;
    } else if (booking.customer?.lastName) {
      return booking.customer.lastName;
    } else if (booking.customer?.email) {
      return booking.customer.email;
    } else {
      return 'Unknown Customer';
    }
  };

  // Get technician name
  const getTechnicianName = (technicianId: string) => {
    const technician = technicians.find(t => t.id === technicianId);
    return technician ? `${technician.firstName} ${technician.lastName}` : 'Unassigned';
  };

  // Format amount
  const formatAmount = (amount: number | undefined) => `$${((amount ?? 0) / 100).toFixed(2)}`;

  const calendarDays = getCalendarDays();
  const selectedDayBookings = selectedDate ? getBookingsForDate(selectedDate) : [];

  // Month View
  const renderMonthView = () => (
    <Box>
      {/* Calendar Header - Days of Week */}
      <Grid templateColumns="repeat(7, 1fr)" gap={2} mb={2}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <GridItem key={day}>
            <Text
              textAlign="center"
              fontWeight="bold"
              fontSize="sm"
              color="gray.600"
              py={2}
            >
              {day}
            </Text>
          </GridItem>
        ))}
      </Grid>

      {/* Calendar Grid */}
      <Grid templateColumns="repeat(7, 1fr)" gap={2}>
        {calendarDays.map((date, index) => {
          const dayBookings = getBookingsForDate(date);
          const isCurrentMonthDay = isCurrentMonth(date);
          const isTodayDay = isToday(date);
          const isSelectedDay = isSelected(date);

          return (
            <GridItem key={index}>
              <Card
                h="120px"
                bg={
                  isSelectedDay ? selectedBg :
                  isTodayDay ? todayBg :
                  bgColor
                }
                borderColor={
                  isSelectedDay ? 'blue.500' :
                  isTodayDay ? 'blue.300' :
                  borderColor
                }
                borderWidth="2px"
                cursor="pointer"
                onClick={() => {
                  setSelectedDate(date);
                  setView('day');
                }}
                _hover={{ bg: hoverBg, transform: 'translateY(-2px)', shadow: 'md' }}
                transition="all 0.2s"
                opacity={isCurrentMonthDay ? 1 : 0.5}
              >
                <CardBody p={2}>
                  <VStack align="stretch" spacing={1} h="full">
                    <HStack justify="space-between">
                      <Text
                        fontSize="sm"
                        fontWeight={isTodayDay ? 'bold' : 'normal'}
                        color={isTodayDay ? 'blue.600' : 'gray.700'}
                      >
                        {date.getDate()}
                      </Text>
                      {dayBookings.length > 0 && (
                        <Badge colorScheme="blue" fontSize="xs">
                          {dayBookings.length}
                        </Badge>
                      )}
                    </HStack>
                    
                    {/* Show booking indicators */}
                    <VStack align="stretch" spacing={0.5} flex={1} overflow="hidden">
                      {dayBookings.slice(0, 3).map((booking, idx) => (
                        <Tooltip
                          key={idx}
                          label={`${getCustomerName(booking)} - ${formatTime(booking.scheduledTime || booking.scheduledDate)}`}
                          placement="top"
                        >
                          <Box
                            px={1}
                            py={0.5}
                            borderRadius="sm"
                            bg={`${getStatusColor(booking.status)}.100`}
                            fontSize="xs"
                            isTruncated
                            onClick={(e) => {
                              e.stopPropagation();
                              onBookingClick(booking);
                            }}
                            _hover={{ bg: `${getStatusColor(booking.status)}.200` }}
                          >
                            <Text isTruncated fontSize="xs">
                              {formatTime(booking.scheduledTime || booking.scheduledDate)}
                            </Text>
                          </Box>
                        </Tooltip>
                      ))}
                      {dayBookings.length > 3 && (
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          +{dayBookings.length - 3} more
                        </Text>
                      )}
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );

  // Day View
  const renderDayView = () => {
    if (!selectedDate) return null;

    return (
      <Box>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md">
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Heading>
          <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
            {selectedDayBookings.length} Booking{selectedDayBookings.length !== 1 ? 's' : ''}
          </Badge>
        </Flex>

        {selectedDayBookings.length === 0 ? (
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Icon as={FiCalendarIcon} boxSize={12} color="gray.400" />
                <Text color="gray.500" fontSize="lg">
                  No bookings scheduled for this day
                </Text>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <VStack spacing={4} align="stretch">
            {selectedDayBookings
              .sort((a, b) => {
                const aTime = a.scheduledTime || a.scheduledDate;
                const bTime = b.scheduledTime || b.scheduledDate;
                
                // Convert Firestore Timestamps to Dates
                let aDate: Date;
                if (aTime && typeof aTime === 'object' && 'toDate' in aTime) {
                  aDate = (aTime as any).toDate();
                } else {
                  aDate = new Date(aTime as any);
                }
                
                let bDate: Date;
                if (bTime && typeof bTime === 'object' && 'toDate' in bTime) {
                  bDate = (bTime as any).toDate();
                } else {
                  bDate = new Date(bTime as any);
                }
                
                return aDate.getTime() - bDate.getTime();
              })
              .map((booking) => (
                <Card
                  key={booking.id}
                  cursor="pointer"
                  onClick={() => onBookingClick(booking)}
                  _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                  borderLeftWidth="4px"
                  borderLeftColor={`${getStatusColor(booking.status)}.500`}
                >
                  <CardBody>
                    <Grid templateColumns={{ base: '1fr', md: 'auto 1fr auto' }} gap={4}>
                      {/* Time */}
                      <VStack align="center" justify="center" minW="100px">
                        <Icon as={FiClockIcon} boxSize={6} />
                        <Text fontWeight="bold" fontSize="lg">
                          {formatTime(booking.scheduledTime || booking.scheduledDate)}
                        </Text>
                        <Badge colorScheme={getStatusColor(booking.status)}>
                          {booking.status}
                        </Badge>
                      </VStack>

                      {/* Booking Details */}
                      <VStack align="stretch" spacing={2}>
                        <HStack justify="space-between">
                          <Heading size="md">{getCustomerName(booking)}</Heading>
                          {booking.poolId && (
                            <Badge colorScheme="purple" variant="subtle">
                              {booking.poolName || 'Pool Booking'}
                            </Badge>
                          )}
                        </HStack>

                        <HStack spacing={4} flexWrap="wrap">
                          <HStack>
                            <Icon as={FiUserIcon} boxSize={4} />
                            <Text fontSize="sm" color="gray.600">
                              {booking.customer?.email || 'No email'}
                            </Text>
                          </HStack>
                          {booking.technicianId && (
                            <HStack>
                              <Icon as={FiUserCheckIcon} boxSize={4} />
                              <Text fontSize="sm" color="gray.600">
                                {getTechnicianName(booking.technicianId)}
                              </Text>
                            </HStack>
                          )}
                        </HStack>

                        <HStack spacing={4} flexWrap="wrap">
                          <HStack>
                            <Icon as={FiMapPinIcon} boxSize={4} />
                            <Text fontSize="sm" color="gray.600" isTruncated>
                              {typeof booking.address === 'string' ? booking.address : 'No address'}
                            </Text>
                          </HStack>
                        </HStack>

                        {booking.notes && (
                          <Text fontSize="sm" color="gray.500" noOfLines={2}>
                            Note: {booking.notes}
                          </Text>
                        )}
                      </VStack>

                      {/* Amount */}
                      <VStack align="center" justify="center" minW="100px">
                        <Icon as={FiDollarSignIcon} boxSize={6} />
                        <Text fontWeight="bold" fontSize="lg" color="green.600">
                          {formatAmount(booking.amount)}
                        </Text>
                        {booking.poolDiscount && (
                          <Text fontSize="xs" color="green.500">
                            -{formatAmount(booking.poolDiscount)} discount
                          </Text>
                        )}
                      </VStack>
                    </Grid>
                  </CardBody>
                </Card>
              ))}
          </VStack>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header Controls */}
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        flexWrap="wrap"
        gap={4}
      >
        <HStack spacing={4}>
          <IconButton
            aria-label="Previous month"
            icon={<Icon as={FiChevronLeftIcon} />}
            onClick={goToPreviousMonth}
            variant="outline"
          />
          <Heading size="md" minW="200px" textAlign="center">
            {formatDate(currentDate)}
          </Heading>
          <IconButton
            aria-label="Next month"
            icon={<Icon as={FiChevronRightIcon} />}
            onClick={goToNextMonth}
            variant="outline"
          />
        </HStack>

        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={goToToday}
          >
            Today
          </Button>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              onClick={() => setView('month')}
              colorScheme={view === 'month' ? 'blue' : 'gray'}
              variant={view === 'month' ? 'solid' : 'outline'}
            >
              Month
            </Button>
            <Button
              onClick={() => {
                setView('day');
                if (!selectedDate) {
                  setSelectedDate(new Date());
                }
              }}
              colorScheme={view === 'day' ? 'blue' : 'gray'}
              variant={view === 'day' ? 'solid' : 'outline'}
            >
              Day
            </Button>
          </ButtonGroup>
        </HStack>
      </Flex>

      <Divider mb={6} />

      {/* Calendar Content */}
      {view === 'month' ? renderMonthView() : renderDayView()}
    </Box>
  );
};

export default BookingsCalendar;

